import { toHref, toReleasePolicy, toReleaseSettings } from '@gonfalon/navigator/navigators';
import type { ButtonProps, PopoverProps } from '@launchpad-ui/components';
import {
  Button,
  Menu as LDMenu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

/** Release types the automated-rollout UI currently supports. */
export type RolloutReleaseType = 'simple' | 'policy';

export const DEFAULT_ROLLOUT_RELEASE_TYPE: RolloutReleaseType = 'simple';

const RELEASE_TYPE_OPTIONS: ReadonlyArray<{ value: RolloutReleaseType; label: string }> = [
  { value: 'simple', label: 'Simple' },
  { value: 'policy', label: 'Policy' },
];

export interface RolloutEnvironment {
  key: string;
  name: string;
}

/** Minimal info about the applicable policy for a given environment, used for
 *  disabling/enabling the policy button and linking to the policy page.
 *  `hasStages` is false when the policy is not ready to release — either because
 *  the release method requires stages (progressive/guarded) but none are
 *  configured, or because a guarded release requires metrics but none are
 *  configured. The "Policy" option is disabled in that case since the scheduler
 *  would fail at release time.
 *  `disabledReason` carries a short explanation for the tooltip when
 *  `hasStages` is false. */
export interface ApplicablePolicyInfo {
  key: string;
  name: string;
  hasStages: boolean;
  disabledReason?: string;
}

export interface RolloutEnvironmentSelectorProps {
  environments: RolloutEnvironment[];
  selectedKeys: string[];
  onSelectionChange: (keys: string[]) => void;
  /** Per-environment release type, keyed by environment key. Missing entries
   *  default to {@link DEFAULT_ROLLOUT_RELEASE_TYPE}. */
  releaseTypes?: Record<string, RolloutReleaseType>;
  /** Called when the user changes the release type for an environment. */
  onReleaseTypeChange?: (environmentKey: string, releaseType: RolloutReleaseType) => void;
  /** Per-environment applicable policy info, keyed by environment key. When an
   *  entry is undefined, the "Policy" release type is disabled for that env. */
  releasePolicyByEnvKey?: Record<string, ApplicablePolicyInfo | undefined>;
  /** Project key, used to construct links to the release policy page. */
  projectKey?: string;
  /** Popover placement relative to the trigger. Defaults to "top right". */
  placement?: PopoverProps['placement'];
  disabled?: boolean;
  triggerClassName?: string;
  size?: ButtonProps['size'];
}

/**
 * Multi-select for the environments Vega should automatically release the new
 * flag in (Vega "automated rollout" mode), plus a per-environment release-type
 * picker ("simple" or "policy"). Modeled on GitHubRepoSelector: a minimal
 * trigger button plus a popover.
 *
 * "Simple" turns the flag on immediately. "Policy" waits until after PR merge
 * and resolves the environment's configured release policy at release time.
 * The "Policy" button is disabled for environments with no applicable policy.
 */
export function RolloutEnvironmentSelector({
  environments,
  selectedKeys,
  onSelectionChange,
  releaseTypes = {},
  onReleaseTypeChange,
  releasePolicyByEnvKey = {},
  projectKey,
  placement = 'top right',
  disabled,
  triggerClassName,
  size = 'small',
}: RolloutEnvironmentSelectorProps) {
  const envKeys = environments.map((env) => env.key);
  const effectiveSelected = selectedKeys.filter((key) => envKeys.includes(key));
  const allSelected = envKeys.length > 0 && effectiveSelected.length === envKeys.length;

  const selectedCount = effectiveSelected.length;
  let label: string;
  if (selectedCount === 0) {
    label = 'Select environments';
  } else if (selectedCount === 1) {
    const env = environments.find((e) => effectiveSelected.includes(e.key));
    label = env?.name ?? '1 environment';
  } else {
    label = `${selectedCount} environments`;
  }

  const handleToggleAll = () => {
    onSelectionChange(allSelected ? [] : envKeys);
  };

  const selectedEnvironments = environments.filter((env) => effectiveSelected.includes(env.key));

  return (
    <MenuTrigger>
      <Button variant="minimal" size={size} isDisabled={disabled} className={triggerClassName}>
        {label}
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover placement={placement}>
        <div style={{ minWidth: 260 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 8 }}>
            <Button variant="minimal" size="small" onPress={handleToggleAll}>
              {allSelected ? 'Clear all' : 'Select all'}
            </Button>
          </div>
          <LDMenu
            selectionMode="multiple"
            selectedKeys={effectiveSelected}
            onSelectionChange={(keys) => {
              if (keys === 'all') {
                onSelectionChange(envKeys);
                return;
              }
              onSelectionChange(Array.from(keys).map(String));
            }}
            style={{ maxHeight: '40vh' }}
          >
            <MenuSection>
              {environments.map((env) => (
                <MenuItem key={env.key} id={env.key} textValue={env.name}>
                  {env.name}
                </MenuItem>
              ))}
            </MenuSection>
          </LDMenu>

          {selectedEnvironments.length > 0 && (
            <div
              style={{
                borderTop: '1px solid var(--lp-color-border-ui-secondary, #d9d9d9)',
                padding: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600 }}>Release type</div>
              {selectedEnvironments.map((env) => {
                const current = releaseTypes[env.key] ?? DEFAULT_ROLLOUT_RELEASE_TYPE;
                const applicablePolicy = releasePolicyByEnvKey[env.key];
                const policyUrl =
                  projectKey && applicablePolicy
                    ? toHref(toReleasePolicy({ projectKey, releasePolicyKey: applicablePolicy.key }))
                    : projectKey
                      ? toHref(toReleaseSettings({ projectKey }))
                      : undefined;

                return (
                  <div
                    key={env.key}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                      <span
                        style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {env.name}
                      </span>
                      {policyUrl && (
                        <a
                          href={policyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'var(--lp-color-text-interactive-secondary, #5c5f61)',
                          }}
                          title={applicablePolicy ? `View policy: ${applicablePolicy.name}` : 'Release settings'}
                        >
                          <Icon name="arrow-up-right" size="small" />
                        </a>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {RELEASE_TYPE_OPTIONS.map((option) => {
                        const isPolicyOption = option.value === 'policy';
                        const noPolicyConfigured = isPolicyOption && !applicablePolicy;
                        const policyMissingStages = isPolicyOption && applicablePolicy && !applicablePolicy.hasStages;
                        const policyDisabled = noPolicyConfigured || policyMissingStages;
                        const isDisabled = disabled || !onReleaseTypeChange || policyDisabled;

                        const button = (
                          <Button
                            key={option.value}
                            size="small"
                            variant={current === option.value ? 'primary' : 'minimal'}
                            isDisabled={isDisabled}
                            aria-pressed={current === option.value}
                            onPress={() => onReleaseTypeChange?.(env.key, option.value)}
                          >
                            {option.label}
                          </Button>
                        );

                        if (isPolicyOption && policyDisabled) {
                          const tooltipText = policyMissingStages
                            ? `Release policy "${applicablePolicy?.name}" has ${applicablePolicy?.disabledReason ?? 'no steps configured'}`
                            : 'No release policy configured for this environment';
                          return (
                            <TooltipTrigger key={option.value}>
                              {button}
                              <Tooltip>{tooltipText}</Tooltip>
                            </TooltipTrigger>
                          );
                        }

                        return button;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Popover>
    </MenuTrigger>
  );
}
